import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Loader2, Lock, RotateCcw, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { authFetch, clearAuth, getUser } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { useLang, LangToggle } from "@/components/landing/i18n";

const PERSONALITY = { social_energy: "Social Energy", thinking: "Thinking Style", decision: "Decision-Making", planning: "Planning & Adaptability", resilience: "Resilience" };
const INTERESTS = { tech: "Technology & Computing", engineering: "Engineering & Manufacturing", science: "Pure & Applied Sciences", business: "Business & Management", finance: "Finance & Economics", entrepreneurship: "Entrepreneurship", law: "Law & Governance", psych: "Psychology & Human Services", education: "Education & Teaching", healthcare: "Healthcare & Medicine", media: "Media & Communication", design: "Design, Architecture & Arts", public_affairs: "Public Administration & Policy", social_impact: "Social Impact & Development" };
const PERSONALITY_HI = { social_energy: "सामाजिक ऊर्जा", thinking: "सोचने की शैली", decision: "निर्णय-निर्माण", planning: "योजना और अनुकूलनशीलता", resilience: "लचीलापन और आत्मविश्वास" };
const INTERESTS_HI = { tech: "प्रौद्योगिकी", engineering: "इंजीनियरिंग", science: "विज्ञान", business: "बिज़नेस", finance: "वित्त", entrepreneurship: "उद्यमिता", law: "कानून", psych: "मनोविज्ञान और मानव सेवाएँ", education: "शिक्षा", healthcare: "स्वास्थ्य सेवा", media: "मीडिया और संचार", design: "डिज़ाइन", public_affairs: "लोक मामले", social_impact: "सामाजिक प्रभाव" };
const ACCENTS = ["#1F5BFF", "#17B6E0", "#3AA64B", "#F5A524", "#E84393", "#7C3AED"];

const PROG_UI = {
  en: {
    dashboard: "Dashboard",
    gateTitle: "Your progress lives here",
    gateCopy: "Sign in to compare Discovery reports over time.",
    signin: "Sign in",
    loadFail: "Could not load progress",
    lockedTitle: "One more Discovery unlocks progress",
    lockedCopy: "Retake the Discovery Assessment later in your journey to see how your aptitude, interests and personality patterns change.",
    latest: "Latest",
    aptitude: "aptitude",
    openDiscovery: "Open Discovery",
    stripLabel: "Discovery progress",
    comparedWith: "Compared with",
    from: "from",
    reports: "Reports",
    aptitudeCap: "Aptitude",
    change: "Change",
    aptitudeOverTime: "Aptitude over time",
    reportTimeline: "Report timeline",
    biggestChanges: "Biggest changes",
    biggestChangesSub: "Latest Discovery compared with the previous report.",
    personalityMovement: "Personality movement",
    interestMovement: "Interest movement",
    viewLatest: "View latest profile",
    groupPersonality: "Personality",
    groupInterest: "Interest",
    dateLocale: "en-GB",
  },
  hi: {
    dashboard: "डैशबोर्ड",
    gateTitle: "आपकी प्रगति यहाँ रहती है",
    gateCopy: "समय के साथ डिस्कवरी रिपोर्ट की तुलना करने के लिए साइन इन करें।",
    signin: "साइन इन",
    loadFail: "प्रगति लोड नहीं हो सकी",
    lockedTitle: "प्रगति खोलने के लिए एक और डिस्कवरी चाहिए",
    lockedCopy: "अपनी यात्रा में बाद में डिस्कवरी आकलन फिर से दें और देखें कि आपकी योग्यता, रुचियाँ और व्यक्तित्व पैटर्न कैसे बदलते हैं।",
    latest: "नवीनतम",
    aptitude: "योग्यता",
    openDiscovery: "डिस्कवरी खोलें",
    stripLabel: "डिस्कवरी प्रगति",
    comparedWith: "तुलना",
    from: "से",
    reports: "रिपोर्ट",
    aptitudeCap: "योग्यता",
    change: "बदलाव",
    aptitudeOverTime: "समय के साथ योग्यता",
    reportTimeline: "रिपोर्ट टाइमलाइन",
    biggestChanges: "सबसे बड़े बदलाव",
    biggestChangesSub: "नवीनतम डिस्कवरी की पिछली रिपोर्ट से तुलना।",
    personalityMovement: "व्यक्तित्व में बदलाव",
    interestMovement: "रुचियों में बदलाव",
    viewLatest: "नवीनतम प्रोफ़ाइल देखें",
    groupPersonality: "व्यक्तित्व",
    groupInterest: "रुचि",
    dateLocale: "hi-IN",
  },
};

const Delta = ({ value }) => {
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${value >= 0 ? "bg-iqgreen/10 text-iqgreen" : "bg-iqpink/10 text-iqpink"}`}><Icon className="h-3 w-3" />{value > 0 ? "+" : ""}{value}</span>;
};

const ShiftBar = ({ label, value, delta, color }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs font-semibold text-ink/65 truncate">{label}</span>
      <span className="flex items-center gap-2 shrink-0"><Delta value={delta} /><b className="text-xs">{value}%</b></span>
    </div>
    <div className="h-2.5 rounded-full bg-ink/8 overflow-hidden">
      <motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 0.8 }} />
    </div>
  </div>
);

export default function ProgressPage() {
  const { lang } = useLang();
  const pui = PROG_UI[lang] || PROG_UI.en;
  const PERSONALITY_L = lang === "hi" ? PERSONALITY_HI : PERSONALITY;
  const INTERESTS_L = lang === "hi" ? INTERESTS_HI : INTERESTS;
  const fmtDate = (iso) => new Date(iso).toLocaleDateString(pui.dateLocale, { day: "2-digit", month: "short", year: "numeric" });
  const [user, setUser] = useState(getUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    if (!user) return;
    authFetch(`/api/discovery/reports?lang=${lang}`).then(async (r) => {
      if (r.status === 401) { clearAuth(); setUser(null); return; }
      if (!r.ok) { toast.error(pui.loadFail); return; }
      setReports(await r.json());
    });
  }, [user, lang]);

  const chronological = useMemo(() => [...(reports || [])].reverse(), [reports]);
  const latest = reports?.[0];
  const previous = reports?.[1];
  const hasProgress = Boolean(latest && previous);

  const shifts = useMemo(() => {
    if (!hasProgress) return [];
    const rows = [
      ...Object.keys(PERSONALITY_L).map((k) => ({ group: pui.groupPersonality, label: PERSONALITY_L[k], delta: (latest.personality?.[k] || 0) - (previous.personality?.[k] || 0) })),
      ...Object.keys(INTERESTS_L).map((k) => ({ group: pui.groupInterest, label: INTERESTS_L[k], delta: (latest.interests?.[k] || 0) - (previous.interests?.[k] || 0) })),
    ];
    return rows.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 6);
  }, [hasProgress, latest, previous, lang]);

  const chart = useMemo(() => {
    const vals = chronological.map((r) => r.aptitude_overall || 0);
    if (vals.length < 2) return null;
    const w = 720, h = 180, pad = 30;
    const points = vals.map((v, i) => [pad + (i * (w - pad * 2)) / (vals.length - 1), h - pad - (v / 100) * (h - pad * 2)]);
    return { w, h, points, vals };
  }, [chronological]);

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="progress-page">
      <header className="max-w-5xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="progress-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <LangToggle />
          {user && <Link to="/dashboard" data-testid="progress-dashboard-link" className="text-sm font-semibold text-iqblue">{pui.dashboard}</Link>}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-12">
        {!user && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="progress-gate">
            <span className="inline-flex h-14 w-14 rounded-2xl bg-iqblue/10 items-center justify-center"><Lock className="h-6 w-6 text-iqblue" /></span>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-5">{pui.gateTitle}</h1>
            <p className="mt-3 text-sm text-ink/55 max-w-md mx-auto">{pui.gateCopy}</p>
            <button onClick={() => setAuthOpen(true)} data-testid="progress-signin-button" className="mt-6 bg-ink text-paper font-semibold px-8 py-3.5 rounded-full hover:bg-iqblue transition-colors">{pui.signin}</button>
          </div>
        )}

        {user && !reports && <div className="py-24 flex justify-center" data-testid="progress-loading"><Loader2 className="h-8 w-8 animate-spin text-iqblue" /></div>}

        {user && reports && !hasProgress && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="progress-locked">
            <span className="inline-flex h-14 w-14 rounded-2xl bg-iqamber/15 items-center justify-center"><Compass className="h-6 w-6 text-iqamber" /></span>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-5">{pui.lockedTitle}</h1>
            <p className="mt-3 text-sm text-ink/55 max-w-xl mx-auto">{pui.lockedCopy}</p>
            {latest && <p className="mt-4 text-xs text-ink/45">{pui.latest}: {latest.archetype} · {latest.aptitude_overall}% {pui.aptitude} · {fmtDate(latest.created_at)}</p>}
            <Link to="/discovery" data-testid="progress-retake-link" className="mt-7 inline-flex items-center gap-2 bg-ink text-paper font-semibold px-7 py-3.5 rounded-full hover:bg-iqblue transition-colors"><RotateCcw className="h-4 w-4" /> {pui.openDiscovery}</Link>
          </div>
        )}

        {user && hasProgress && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="progress-content">
            <div className="rounded-[2rem] bg-ink text-paper p-8 sm:p-10 relative overflow-hidden">
              <div className="absolute inset-0 spotlight-glow pointer-events-none" aria-hidden />
              <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-iqcyan">{pui.stripLabel}</div>
                  <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-3">{latest.archetype}</h1>
                  <p className="mt-2 text-sm text-paper/65">{pui.comparedWith} {previous.archetype} {pui.from} {fmtDate(previous.created_at)}</p>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-2xl bg-white/10 border border-white/10 px-4 py-3"><div className="font-display font-bold text-2xl">{reports.length}</div><div className="text-[10px] uppercase tracking-wider text-paper/50">{pui.reports}</div></div>
                  <div className="rounded-2xl bg-white/10 border border-white/10 px-4 py-3"><div className="font-display font-bold text-2xl">{latest.aptitude_overall}%</div><div className="text-[10px] uppercase tracking-wider text-paper/50">{pui.aptitudeCap}</div></div>
                  <div className="rounded-2xl bg-white/10 border border-white/10 px-4 py-3"><Delta value={latest.aptitude_overall - previous.aptitude_overall} /><div className="text-[10px] uppercase tracking-wider text-paper/50 mt-1">{pui.change}</div></div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-[1.25fr_0.75fr] gap-6">
              <section className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid="progress-aptitude-chart">
                <h2 className="font-display font-bold text-xl tracking-tight">{pui.aptitudeOverTime}</h2>
                <div className="relative mt-5">
                  <svg viewBox={`0 0 ${chart.w} ${chart.h}`} className="w-full h-44" role="img" aria-label="Aptitude trend">
                    <polyline points={chart.points.map((p) => p.join(",")).join(" ")} fill="none" stroke="#1F5BFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                    {chart.points.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="7" fill="#1F5BFF" />)}
                  </svg>
                  {chart.points.map(([x, y], i) => (
                    <span
                      key={`label-${i}`}
                      data-testid={`progress-chart-label-${i}`}
                      className="absolute z-10 rounded-full bg-paper border border-ink/10 px-2.5 py-1 text-xs font-bold text-ink shadow-sm whitespace-nowrap"
                      style={{ left: `${(x / chart.w) * 100}%`, top: `${(y / chart.h) * 100}%`, transform: y < chart.h * 0.35 ? "translate(-50%, 12px)" : "translate(-50%, calc(-100% - 12px))" }}
                    >
                      {chart.vals[i]}%
                    </span>
                  ))}
                </div>
              </section>
              <section className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid="progress-timeline">
                <h2 className="font-display font-bold text-xl tracking-tight">{pui.reportTimeline}</h2>
                <div className="mt-5 space-y-4">
                  {chronological.map((r, i) => (
                    <div key={r.id} className="flex gap-3" data-testid={`progress-timeline-item-${i}`}>
                      <span className="mt-1 h-3 w-3 rounded-full shrink-0" style={{ background: ACCENTS[i % ACCENTS.length] }} />
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{r.archetype}</div>
                        <div className="text-xs text-ink/45 mt-0.5">{fmtDate(r.created_at)} · {r.aptitude_overall}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <section className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid="progress-top-shifts">
              <div className="flex items-center justify-between gap-4">
                <div><h2 className="font-display font-bold text-xl tracking-tight">{pui.biggestChanges}</h2><p className="text-xs text-ink/50 mt-1">{pui.biggestChangesSub}</p></div>
                <Delta value={latest.aptitude_overall - previous.aptitude_overall} />
              </div>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {shifts.map((s, i) => (
                  <div key={`${s.group}-${s.label}`} className="rounded-2xl bg-white border border-ink/8 p-4" data-testid={`progress-shift-card-${i}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink/40">{s.group}</div>
                    <div className="mt-1 font-semibold text-sm">{s.label}</div>
                    <div className="mt-3"><Delta value={s.delta} /></div>
                  </div>
                ))}
              </div>
            </section>

            <div className="grid lg:grid-cols-2 gap-6">
              <section className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid="progress-personality">
                <h2 className="font-display font-bold text-xl tracking-tight mb-5">{pui.personalityMovement}</h2>
                <div className="space-y-4">
                  {Object.keys(PERSONALITY_L).map((k, i) => <ShiftBar key={k} label={PERSONALITY_L[k]} value={latest.personality?.[k] || 0} delta={(latest.personality?.[k] || 0) - (previous.personality?.[k] || 0)} color={ACCENTS[i % ACCENTS.length]} />)}
                </div>
              </section>
              <section className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid="progress-interests">
                <h2 className="font-display font-bold text-xl tracking-tight mb-5">{pui.interestMovement}</h2>
                <div className="space-y-4">
                  {Object.entries(latest.interests || {}).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, v], i) => <ShiftBar key={k} label={INTERESTS_L[k] || k} value={v} delta={v - (previous.interests?.[k] || 0)} color={ACCENTS[(i + 2) % ACCENTS.length]} />)}
                </div>
              </section>
            </div>

            <div className="flex justify-center">
              <Link to={`/discovery/report/${latest.id}`} data-testid="progress-latest-report" className="inline-flex items-center gap-2 bg-ink text-paper font-semibold px-7 py-3.5 rounded-full hover:bg-iqblue transition-colors">{pui.viewLatest} <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </motion.div>
        )}
      </main>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={(u) => setUser(u)} />
    </div>
  );
}
